/**
 * GSTN's last generated summary — shown, printed and handed over as it stands.
 * @file src/web/features/admin/components/PortalSummary.tsx
 *
 * Not a second render of our figures. Every number here came back from the
 * portal, including the checksum the signature is checked against, and
 * nothing is recomputed on the way to the screen or to the file that leaves
 * it. That is the whole point: a CA sending a client "this is what will be
 * filed" should be sending the portal's words, not ours.
 *
 * The GST portal offers the same thing as a summary PDF before the
 * irreversible step. This is that, from the summary the portal last built.
 */

import { Printer, Download, FileJson } from 'lucide-react';

/** GSTN's section codes in the words a CA uses. Unmapped shows its own code. */
export const SECTION_NAME: Record<string, string> = {
  B2B: 'B2B', B2BA: 'B2B amendments', B2CL: 'B2C large', B2CLA: 'B2C large amendments',
  B2CS: 'B2C small', B2CSA: 'B2C small amendments', EXP: 'Exports', EXPA: 'Export amendments',
  CDNR: 'Credit/debit notes', CDNRA: 'CDN amendments', CDNUR: 'CDN unregistered', CDNURA: 'CDNUR amendments',
  NIL: 'Nil-rated', AT: 'Advances', ATA: 'Advance amendments', TXPD: 'Advances adjusted', TXPDA: 'Adjustment amendments',
  HSN: 'HSN summary', HSN_B2B: 'HSN summary — B2B', HSN_B2C: 'HSN summary — B2C',
  DOC_ISSUE: 'Documents issued', ECOM: 'E-commerce', SUPECOM: 'Supplies via e-commerce',
  TTL_LIAB: 'Total liability',
};

/** GSTN's table numbers, for the sections that carry one. */
const TABLE: Record<string, string> = {
  B2B: '4A, 4B, 6B, 6C', B2CL: '5A', B2CS: '7', EXP: '6A',
  CDNR: '9B', CDNUR: '9B', NIL: '8A–8D', AT: '11A', TXPD: '11B',
  HSN: '12', HSN_B2B: '12', HSN_B2C: '12', DOC_ISSUE: '13',
};

/* The per-rate breakdowns GSTN repeats beneath a parent section. Listing them
   would show every figure twice; the parent already carries the total. */
const DERIVED = /_(4A|4B|6C|SEZWOP|SEZWP|DE|REG|UNREG)$/;

export type PortalRow = { section: string; records: number; taxable: number; value: number; tax: number };

const money = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fpLabel = (fp: string) => (/^\d{6}$/.test(fp) ? `${fp.slice(0, 2)}/${fp.slice(2)}` : fp || '—');
const stamp = (iso: string) => new Date(iso).toLocaleString('en-IN');

export function PortalSummary({ gstin, fp, rows, chksum, readAt, payload, filed = false, bare = false }: {
  gstin: string | null;
  fp: string;
  /** Already filed: the print says so, instead of marking it a draft. */
  filed?: boolean;
  /** Rendered inside a card that already has the heading: no second card and
   *  no second title. Four nested boxes around one table is three too many. */
  bare?: boolean;
  /** Every section GSTN counted something in, as GSTN counted it. */
  rows: PortalRow[];
  chksum: string;
  readAt: string;
  /** GSTN's summary object whole, for the JSON download. */
  payload?: unknown;
}) {
  const shown = rows.filter((r) => !DERIVED.test(r.section) && r.section !== 'TTL_LIAB');
  const total = rows.find((r) => r.section === 'TTL_LIAB') ?? null;

  const head = `GSTR-1 summary as generated by GSTN · GSTIN ${gstin ?? '— not set'} · return period ${fpLabel(fp)} · read ${stamp(readAt)} · checksum ${chksum}`;

  const print = () => {
    const line = (r: PortalRow) => `<tr>
      <td>${TABLE[r.section] ?? ''}</td>
      <td>${SECTION_NAME[r.section] ?? r.section}</td>
      <td class="n">${r.records}</td>
      <td class="n">${money(r.taxable)}</td>
      <td class="n">${money(r.tax)}</td>
      <td class="n">${money(r.value)}</td>
    </tr>`;
    const doc = `<!doctype html><html><head><meta charset="utf-8">
      <title>GSTR-1 summary ${fpLabel(fp)} ${gstin ?? ''}</title>
      <style>
        body{font:12px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;color:#111;margin:32px}
        h1{font-size:16px;margin:0 0 2px}
        .meta{color:#555;margin:0 0 4px}
        .sum{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:10px;color:#666;word-break:break-all;margin:0 0 14px}
        .draft{display:inline-block;border:1px solid #b45309;color:#b45309;border-radius:999px;padding:2px 10px;font-size:10px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;margin-bottom:14px}
        table{border-collapse:collapse;width:100%}
        th,td{border-bottom:1px solid #e5e5e5;padding:6px 8px;text-align:left;vertical-align:top}
        th{font-size:10px;text-transform:uppercase;letter-spacing:.04em;color:#666;border-bottom:1px solid #999}
        td.n,th.n{text-align:right;font-variant-numeric:tabular-nums}
        tfoot td{font-weight:700;border-top:1px solid #999;border-bottom:none}
        .foot{color:#666;font-size:10px;margin-top:24px;border-top:1px solid #e5e5e5;padding-top:8px}
      </style></head><body>
      <h1>GSTR-1 — ${filed ? 'as filed with GSTN' : 'summary generated by GSTN'}</h1>
      <p class="meta">GSTIN ${gstin ?? '— not set'} &middot; return period ${fpLabel(fp)} &middot; read from the portal ${stamp(readAt)}</p>
      <p class="sum">checksum ${chksum}</p>
      ${filed ? '' : '<p class="draft">Not filed</p>'}
      <table>
        <thead><tr>
          <th>Table</th><th>Section</th><th class="n">Records</th>
          <th class="n">Taxable value</th><th class="n">Tax</th><th class="n">Invoice value</th>
        </tr></thead>
        <tbody>${shown.map(line).join('')}</tbody>
        ${total ? `<tfoot><tr>
          <td></td><td>Total liability</td><td class="n">${total.records}</td>
          <td class="n">${money(total.taxable)}</td><td class="n">${money(total.tax)}</td><td class="n">${money(total.value)}</td>
        </tr></tfoot>` : ''}
      </table>
      <p class="foot">These are the portal's own figures, read from GSTN and not recomputed. Printed from oddits on ${stamp(new Date().toISOString())}.${filed ? '' : ' No return is filed until GSTN returns an ARN.'}</p>
      </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) return;
    w.document.write(doc);
    w.document.close();
    w.focus();
    w.print();
  };

  const save = (text: string, ext: string, type: string) => {
    const url = URL.createObjectURL(new Blob([text], { type }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `GSTR1-portal-summary_${gstin ?? 'entity'}_${fp || 'period'}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* A BOM, because Excel reads a UTF-8 CSV as Latin-1 without one and turns
     every ₹ into mojibake. */
  const csv = () => {
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [
      ['Table', 'Section', 'Records', 'Taxable value', 'Tax', 'Invoice value'].map(esc).join(','),
      ...shown.map((r) => [TABLE[r.section] ?? '', SECTION_NAME[r.section] ?? r.section,
        r.records, r.taxable.toFixed(2), r.tax.toFixed(2), r.value.toFixed(2)].map(esc).join(',')),
      ...(total ? [['', 'Total liability', total.records, total.taxable.toFixed(2), total.tax.toFixed(2), total.value.toFixed(2)].map(esc).join(',')] : []),
      '',
      esc(head),
    ];
    save(`\uFEFF${lines.join('\r\n')}`, 'csv', 'text/csv;charset=utf-8');
  };

  const Shell = bare
    ? ({ children }: { children: React.ReactNode }) => <>{children}</>
    : ({ children }: { children: React.ReactNode }) => <div className="mt-3 rounded-lg border border-ink-200 bg-paper p-3.5">{children}</div>;

  return (
    <Shell>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className={`min-w-0 ${bare ? 'hidden' : ''}`}>
          <p className="text-xs font-semibold text-ink-900">{filed ? 'The return as filed' : 'The summary GSTN generated'}</p>
          <p className="mt-0.5 text-[11px] text-ink-500">
            {filed
              ? 'GSTN\u2019s own figures for the filed return — not our copy of what we sent.'
              : `Read from the portal at ${new Date(readAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} \u2014 its figures, not ours, and not recomputed. This is what the signature is checked against.`}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-1.5">
          <button onClick={print} className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-[11px] font-semibold text-ink-700 hover:bg-ink-50">
            <Printer className="h-3 w-3" /> Print / PDF
          </button>
          <button onClick={csv} className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-[11px] font-semibold text-ink-700 hover:bg-ink-50">
            <Download className="h-3 w-3" /> Excel (CSV)
          </button>
          {payload != null && (
            <button onClick={() => save(JSON.stringify(payload, null, 2), 'json', 'application/json')}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-[11px] font-semibold text-ink-700 hover:bg-ink-50">
              <FileJson className="h-3 w-3" /> JSON
            </button>
          )}
        </div>
      </div>

      <div className={`overflow-x-auto rounded-lg border border-ink-200 ${bare ? 'mt-2' : 'mt-3'}`}>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-ink-200 text-left text-[10px] uppercase tracking-wide text-ink-400">
              <th className="px-3 py-2 font-medium">Table</th>
              <th className="px-3 py-2 font-medium">Section</th>
              <th className="px-3 py-2 text-right font-medium">Records</th>
              <th className="px-3 py-2 text-right font-medium">Taxable</th>
              <th className="px-3 py-2 text-right font-medium">Tax</th>
              <th className="px-3 py-2 text-right font-medium">Invoice value</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.section} className="border-b border-ink-100 last:border-0">
                <td className="px-3 py-2 font-mono text-[10px] text-ink-400">{TABLE[r.section] ?? '—'}</td>
                <td className="px-3 py-2 text-ink-800">{SECTION_NAME[r.section] ?? r.section}</td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-500">{r.records}</td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-800">₹{money(r.taxable)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-600">₹{money(r.tax)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-ink-600">₹{money(r.value)}</td>
              </tr>
            ))}
          </tbody>
          {total && (
            <tfoot>
              <tr className="border-t border-ink-300 bg-ink-50/60 text-xs font-semibold text-ink-900">
                <td className="px-3 py-2" />
                <td className="px-3 py-2">Total liability</td>
                <td className="px-3 py-2 text-right tabular-nums">{total.records}</td>
                <td className="px-3 py-2 text-right tabular-nums">₹{money(total.taxable)}</td>
                <td className="px-3 py-2 text-right tabular-nums">₹{money(total.tax)}</td>
                <td className="px-3 py-2 text-right tabular-nums">₹{money(total.value)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="mt-2 break-all font-mono text-[10px] text-ink-400">checksum {chksum}</p>
    </Shell>
  );
}
